import createClient, { type Middleware } from "openapi-fetch";

import i18n from "@/lib/i18n/i18n";
import { API_ORIGIN } from "@/lib/config";
import { getOnBehalfOf,
  isOnBehalfReadOnly, isOnBehalfScopedPath } from "@/lib/api/on-behalf";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "@/lib/auth/tokens";

import type { paths } from "./schema.gen";

/**
 * Typed API client (openapi-fetch over the generated OpenAPI schema).
 *
 * Auth conventions, implemented once here for every caller:
 *  - inject the in-memory access token as a Bearer header (except on the token
 *    endpoints themselves);
 *  - on a 401, transparently refresh the access token (single-flight: parallel
 *    requests share one refresh) and retry the original request exactly once;
 *  - if refresh fails, clear the session and notify the app so it can redirect
 *    to /login (see setUnauthorizedHandler).
 */

const AUTH_FREE_PATHS = ["/api/v1/auth/token/", "/api/v1/auth/token/refresh/"];

function isAuthFree(url: string): boolean {
  return AUTH_FREE_PATHS.some((p) => url.includes(p));
}

// --- session-expired notification -------------------------------------------

type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** Register a callback fired when the session can no longer be refreshed. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

// --- single-flight refresh ---------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

// Marks requests we've already retried once, so a retry that still 401s isn't
// refreshed-and-retried again (guards against an infinite loop). Tracked in an
// in-memory WeakSet rather than a wire header: it's purely client-side state, so
// sending it to the server is pointless and — being a non-safelisted header —
// would force a CORS preflight the API must explicitly allow. Keeping it off the
// wire avoids that coupling entirely. WeakSet lets retried Requests be GC'd.
const retriedRequests = new WeakSet<Request>();

/**
 * Exchange the refresh token for a fresh access token. Uses plain fetch (not
 * the middleware client) to avoid recursing through the 401 handler. Concurrent
 * callers share the same in-flight promise.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refresh = getRefreshToken();
  if (!refresh) return Promise.resolve(null);

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${API_ORIGIN}/api/v1/auth/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      // The session can change hands while this request is in flight, and the
      // enrolment verification link is how: it mints a pair and stores it
      // directly, never going through `login()`. On a fresh document load the
      // access token is gone but the *previous* user's refresh is still in
      // storage, so `/me` 401s and starts this refresh before the link has
      // been consumed.
      //
      // This outcome then belongs to a session that is no longer current, and
      // that is true of **every** outcome, not just the successful one. The
      // destructive paths matter more, in fact: a shared browser is exactly
      // where the previous refresh token is likely to be expired or already
      // blacklisted (7 days, `ROTATE_REFRESH_TOKENS` +
      // `BLACKLIST_AFTER_ROTATION`), so the common shape of this race is a
      // *failed* refresh whose `clearTokens()` erases the pair the link just
      // stored and bounces the participant to a sign-in screen they have no
      // password for.
      //
      // Hand back the current access token rather than `null`: the caller
      // reads `null` as "this session is finished" and calls `clearTokens()`
      // itself, which would undo the handover just as surely.
      const sessionChangedHands = () => getRefreshToken() !== refresh;
      if (sessionChangedHands()) return getAccessToken();
      if (!res.ok) {
        clearTokens();
        return null;
      }
      // `res.json()` is another suspension point, so the check is repeated
      // rather than hoisted: the handover can land during it.
      const data = (await res.json()) as { access: string; refresh?: string };
      if (sessionChangedHands()) return getAccessToken();
      // ROTATE_REFRESH_TOKENS is on, so the response may carry a new refresh.
      setTokens({ access: data.access, refresh: data.refresh ?? undefined });
      return data.access;
    } catch {
      // Network error: don't nuke the session for a transient failure — and if
      // the session changed hands during it, say so, because the caller's
      // `null` handling would clear the new owner's tokens.
      return getRefreshToken() !== refresh ? getAccessToken() : null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// --- middleware ---------------------------------------------------------------

const SAFE_HTTP_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Thrown instead of sending a write from a consultation-only on-behalf
 * context. A bug in a screen, not something a user can provoke, so it carries
 * what was attempted rather than a translated message. */
export class ReadOnlyContextError extends Error {
  constructor(method: string, url: string) {
    super(`${method} ${url} attempted in a read-only on-behalf context`);
    this.name = "ReadOnlyContextError";
  }
}

export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    // The API serves translated content (questionnaire text, etc.) per
    // Accept-Language; forward the active UI language.
    if (i18n.language) {
      request.headers.set("Accept-Language", i18n.language);
    }

    // Partner acting on a member's data: append ?on_behalf_of on member-scoped
    // paths so the backend resolves the target member (see @/lib/api/on-behalf).
    const onBehalfOf = getOnBehalfOf();
    let outgoing = request;
    if (
      onBehalfOf &&
      isOnBehalfScopedPath(request.url) &&
      !new URL(request.url).searchParams.has("on_behalf_of")
    ) {
      const url = new URL(request.url);
      url.searchParams.set("on_behalf_of", onBehalfOf);
      // `new Request(url, request)` does NOT carry the body: a Request built
      // from another Request needs `duplex: "half"` for its body stream, and
      // without it Chrome hands back a bodyless request and fails the send
      // with ERR_ALPN_NEGOTIATION_FAILED. Every on-behalf write therefore left
      // empty — the partner edits this rewrite was written for, and the
      // administrator's correction of a participant's profile.
      //
      // Reading the body to an ArrayBuffer first sidesteps the stream, so no
      // `duplex` is needed and the copy is a plain buffer.
      outgoing = SAFE_HTTP_METHODS.has(request.method.toUpperCase())
        ? new Request(url, request)
        : new Request(url, {
            method: request.method,
            headers: request.headers,
            body: await request.arrayBuffer(),
            credentials: request.credentials,
            mode: request.mode,
            redirect: request.redirect,
            referrer: request.referrer,
            integrity: request.integrity,
          });
    }

    // A consultation-only context does not write. The server refuses a partner
    // without edit consent, but an administrator bypasses consent by design —
    // so without this the read-only screens an administrator opens a
    // participant's data on would have sent the write and had it succeed.
    if (
      isOnBehalfReadOnly() &&
      isOnBehalfScopedPath(outgoing.url) &&
      !SAFE_HTTP_METHODS.has(outgoing.method.toUpperCase())
    ) {
      throw new ReadOnlyContextError(outgoing.method, outgoing.url);
    }

    if (isAuthFree(outgoing.url)) return outgoing;
    const token = getAccessToken();
    if (token) {
      outgoing.headers.set("Authorization", `Bearer ${token}`);
    }
    return outgoing;
  },

  async onResponse({ request, response }) {
    if (response.status !== 401 || isAuthFree(request.url)) {
      return undefined;
    }

    // Don't attempt a refresh-and-retry for a request that was already retried.
    if (retriedRequests.has(request)) {
      clearTokens();
      onUnauthorized?.();
      return undefined;
    }

    const newAccess = await refreshAccessToken();
    if (!newAccess) {
      clearTokens();
      onUnauthorized?.();
      return undefined;
    }

    const retried = new Request(request, {
      headers: new Headers(request.headers),
    });
    retried.headers.set("Authorization", `Bearer ${newAccess}`);
    retriedRequests.add(retried);
    return fetch(retried);
  },
};

export const api = createClient<paths>({
  baseUrl: API_ORIGIN,
  headers: { "Content-Type": "application/json" },
});

api.use(authMiddleware);

/** Narrowed error shape DRF returns ({detail} or field errors). */
export interface ApiError {
  status: number;
  detail?: string;
  fieldErrors?: Record<string, string[]>;
  /**
   * The service's machine-readable refusal code, when it sends one
   * (`forbidden` / `conflict` / `invalid` / `noop`). A caller needs it to
   * report a refusal at the right level — a `noop` is "nothing to do", not a
   * failure, and the Django views make exactly that distinction.
   */
  code?: string;
}

/**
 * The reason the service gave for refusing a write — its `detail`, else the
 * first field error — or nothing, so the caller falls back to its own
 * translated message.
 */
export function refusal(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const { detail, fieldErrors } = err as ApiError;
  return detail ?? Object.values(fieldErrors ?? {})[0]?.[0];
}

/** Normalize an openapi-fetch error payload into an ApiError. */

export function toApiError(status: number, body: unknown): ApiError {
  if (body && typeof body === "object") {
    const obj = body as Record<string, unknown>;
    if (typeof obj.detail === "string") {
      return {
        status,
        detail: obj.detail,
        ...(typeof obj.code === "string" ? { code: obj.code } : {}),
      };
    }
    const fieldErrors: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value)) fieldErrors[key] = value.map(String);
      else if (typeof value === "string") fieldErrors[key] = [value];
    }
    if (Object.keys(fieldErrors).length > 0) return { status, fieldErrors };
  }
  // No reason the service gave us — say nothing rather than inventing one.
  // This used to return a hardcoded English sentence, which every caller then
  // rendered verbatim through `detail ?? t(...)`: the fallback they had
  // written could never run, and a French user got English on any failure
  // whose body was not JSON (a 502 from the proxy, an HTML 500 page). Omitting
  // it hands each caller back to its own translated message.
  return { status };
}
